import { useReadFile } from 'src/hooks/useReadFile'
import { ContractorFormEntity } from 'src/types'
import {
  useCreateRoomUsersCsvPresignedUrlMutation,
  useNotifyFinishedUploadContractorCsvMutation,
  useRegisterContractorMutation,
  useSendSelectEmailMutation,
  SelectEmailContent,
  useDestroyRoomUserMutation,
} from 'src/types/generated'

type CreateContractorsCsvUploadPresignedUrlOutput = {
  s3Url: string
  presignedUrl: string
  jobId: string
  fileName: string
}

type UploadContractorsCsvInput = {
  file: File
  fileName: string
  presignedUrl: string
}

type NotifyFinishedUploadContractorsCsvInput = {
  jobId: string
  s3Url: string
}

type RegisterContractorInput = {
  roomId: number
  formEntity: ContractorFormEntity
}

type SendSelectEmailInput = {
  projectId: string
  roomIds: number[]
}

type DeleteContractorInput = {
  contractorId: number
  roomId: string
}

export const useContractor = () => {
  const [createRoomUsersCsvPresignedUrlMutation] = useCreateRoomUsersCsvPresignedUrlMutation()
  const [notifyFinishedUploadContractorCsvMutation] = useNotifyFinishedUploadContractorCsvMutation()
  const [registerContractorMutation] = useRegisterContractorMutation()
  const [sendSelectEmailMutation] = useSendSelectEmailMutation()
  const [destroyRoomUserMutation] = useDestroyRoomUserMutation()

  const { readFile, getIsUtf8 } = useReadFile()

  const createContractorsCsvUploadPresignedUrl =
    async (): Promise<CreateContractorsCsvUploadPresignedUrlOutput> => {
      const { data } = await createRoomUsersCsvPresignedUrlMutation()
      if (!data) {
        throw new Error('Failed to get presigned url')
      }

      const { s3URL, presignedURL, jobId } = data.presignedURLRoomUsersImport
      // ファイル名はs3URLの / で分割した最後の部分が該当する
      // ex: http://example.com/hoge/fuga.csv => fuga.csv
      const fileName = s3URL.split('/').pop()

      if (!fileName) {
        throw new Error('Failed to get fileName')
      }

      return {
        s3Url: s3URL,
        presignedUrl: presignedURL,
        fileName,
        jobId,
      }
    }

  const uploadContractorsCsv = async (input: UploadContractorsCsvInput) => {
    const { file, fileName, presignedUrl } = input

    const isUtf8 = await getIsUtf8(file)
    const fileContent = await readFile(file, isUtf8 ? 'utf8' : 'sjis')
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: fileContent,
      headers: {
        'Content-Type': 'text/csv',
        'Content-Disposition': `attachment; filename="${fileName}"`,
      },
    })

    if (!response.ok) {
      throw new Error('Failed to S3 upload')
    }
  }

  const notifyFinishedUploadContractorsCsv = async (
    input: NotifyFinishedUploadContractorsCsvInput
  ) => {
    const { jobId, s3Url } = input

    await notifyFinishedUploadContractorCsvMutation({
      variables: { params: { jobId, s3URL: s3Url } },
    })
  }

  const createContractorCode = async () => {
    // TODO: codeの生成はmutationで実行する。挙動の確認のためmutationが出来るまでフロントで生成する
    const code = Math.floor(Math.random() * (1_000_000 - 100_000) + 100_000)
    return `${code}`
  }

  const registerContractor = async (input: RegisterContractorInput) => {
    const { roomId, formEntity } = input

    await registerContractorMutation({
      variables: {
        params: { roomId, ...formEntity },
      },
    })
  }

  const sendInvitationEmail = async ({ projectId, roomIds }: SendSelectEmailInput) => {
    await sendSelectEmailMutation({
      variables: {
        projectId,
        input: {
          roomIds,
          content: SelectEmailContent.Invitation,
        },
      },
    })
  }

  const sendAgreementEmail = async ({ projectId, roomIds }: SendSelectEmailInput) => {
    await sendSelectEmailMutation({
      variables: {
        projectId,
        input: {
          roomIds,
          content: SelectEmailContent.AgreementRequest,
        },
      },
    })
  }

  const deleteContractor = async (input: DeleteContractorInput) => {
    const { contractorId, roomId } = input

    await destroyRoomUserMutation({
      variables: {
        iamUserId: contractorId,
        roomId: Number(roomId),
      },
    })
  }

  return {
    createContractorsCsvUploadPresignedUrl,
    uploadContractorsCsv,
    notifyFinishedUploadContractorsCsv,
    createContractorCode,
    registerContractor,
    sendInvitationEmail,
    sendAgreementEmail,
    deleteContractor,
  }
}
